import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, KeyRound, ArrowRight, Store, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { useForgotPassword, useVerifyOtp, useResetPassword } from '../../api';
import BrandLogo from '../../components/brand/BrandLogo';
import styles from '../login/login.module.css';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState('email'); // 'email' | 'otp' | 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const navigate = useNavigate();

  const forgotPasswordMutation = useForgotPassword();
  const verifyOtpMutation = useVerifyOtp();
  const resetPasswordMutation = useResetPassword();

  const errorMessage = (error, fallback) =>
    error?.response?.data?.error || error?.response?.data?.message || fallback;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    try {
      await forgotPasswordMutation.mutateAsync({ email });
      toast.success('Security OTP sent to your registered email.');
      setStep('otp');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send verification code.'));
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      const data = await verifyOtpMutation.mutateAsync({ email, otp });
      setResetToken(data.resetToken);
      toast.success('Code verified successfully.');
      setStep('reset');
    } catch (error) {
      toast.error(errorMessage(error, 'Invalid or expired OTP code.'));
    }
  };

  const handleResendOtp = async () => {
    try {
      await forgotPasswordMutation.mutateAsync({ email });
      toast.success('A fresh OTP has been sent to your email.');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not resend OTP.'));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    try {
      await resetPasswordMutation.mutateAsync({ resetToken, newPassword });
      toast.success('Password reset successfully. Please sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(errorMessage(error, 'Could not reset password.'));
    }
  };

  return (
    <div className={styles.authPage}>
      {/* Left Brand Hero */}
      <div className={styles.brandHero}>
        <div className={styles.heroBrandWrapper}>
          <BrandLogo variant="lockup" tone="onDark" />
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <Store size={13} />
            <span>Partner Account Recovery</span>
          </div>

          <h1 className={styles.heroTitle}>
            Secure Account <span className={styles.heroHighlight}>Recovery</span>.
          </h1>

          <p className={styles.heroDescription}>
            Follow the automated identity verification process to recover access to your B4BARBER
            salon partner account.
          </p>

          <div className={styles.heroStatsGrid}>
            <div className={styles.heroStatItem}>
              <span className={styles.heroStatValue}>6-Digit</span>
              <span className={styles.heroStatLabel}>One-Time Passcode</span>
            </div>
            <div className={styles.heroStatItem}>
              <span className={styles.heroStatValue}>10 Min</span>
              <span className={styles.heroStatLabel}>Token Validity</span>
            </div>
            <div className={styles.heroStatItem}>
              <span className={styles.heroStatValue}>SHA-256</span>
              <span className={styles.heroStatLabel}>Encrypted Reset</span>
            </div>
          </div>
        </div>

        <div className={styles.heroFooter}>
          <span className={styles.liveDot} />
          <span>B4BARBER Identity Provider • Secure Protocol</span>
        </div>
      </div>

      {/* Right Form Wizard */}
      <div className={styles.formSection}>
        <div className={styles.card}>
          <div className={styles.mobileBrand}>
            <BrandLogo variant="lockup" tone="onDark" />
          </div>

          <div className={styles.cardHeader}>
            <h2 className={styles.formTitle}>Reset Password</h2>
            <p className={styles.formSubtitle}>
              {step === 'email' && 'Enter your salon account email to receive a recovery code'}
              {step === 'otp' && `Enter the 6-digit code delivered to ${email}`}
              {step === 'reset' && 'Create a strong, new password for your partner account'}
            </p>
          </div>

          {/* Stepper Dots */}
          <div className={styles.stepIndicator}>
            <div className={styles.stepItem}>
              <div
                className={`${styles.stepCircle} ${
                  step === 'email' ? styles.stepCircleActive : styles.stepCircleComplete
                }`}
              >
                1
              </div>
              <span className={styles.stepLabel}>Email</span>
            </div>

            <div className={styles.stepItem}>
              <div
                className={`${styles.stepCircle} ${
                  step === 'otp'
                    ? styles.stepCircleActive
                    : step === 'reset'
                      ? styles.stepCircleComplete
                      : ''
                }`}
              >
                2
              </div>
              <span className={styles.stepLabel}>Verify</span>
            </div>

            <div className={styles.stepItem}>
              <div
                className={`${styles.stepCircle} ${step === 'reset' ? styles.stepCircleActive : ''}`}
              >
                3
              </div>
              <span className={styles.stepLabel}>Password</span>
            </div>
          </div>

          {/* Step 1: Request OTP */}
          {step === 'email' && (
            <form onSubmit={handleSendOtp} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="forgot-email">
                  Partner Account Email
                </label>
                <div className={styles.inputWrapper}>
                  <Mail size={17} className={styles.inputIcon} />
                  <input
                    id="forgot-email"
                    type="email"
                    className={styles.inputControl}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="owner@yourbarbershop.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={forgotPasswordMutation.isPending}
              >
                <span>
                  {forgotPasswordMutation.isPending ? 'Sending OTP…' : 'Send Verification OTP'}
                </span>
                <ArrowRight size={17} />
              </button>
            </form>
          )}

          {/* Step 2: Verify OTP */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="forgot-otp">
                  6-Digit Verification Code
                </label>
                <div className={styles.inputWrapper}>
                  <KeyRound size={17} className={styles.inputIcon} />
                  <input
                    id="forgot-otp"
                    type="text"
                    className={styles.inputControl}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="123456"
                    required
                    maxLength={6}
                    style={{ letterSpacing: '0.2em', fontFamily: 'monospace', fontWeight: 700 }}
                  />
                </div>
              </div>

              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Didn't receive code?</span>
                <button
                  type="button"
                  className={styles.link}
                  onClick={handleResendOtp}
                  disabled={forgotPasswordMutation.isPending}
                >
                  Resend Code
                </button>
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={verifyOtpMutation.isPending}
              >
                <span>{verifyOtpMutation.isPending ? 'Verifying…' : 'Verify Code'}</span>
                <ArrowRight size={17} />
              </button>
            </form>
          )}

          {/* Step 3: Set New Password */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className={styles.form}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="new-password">
                  New Password
                </label>
                <div className={styles.inputWrapper}>
                  <Lock size={17} className={styles.inputIcon} />
                  <input
                    id="new-password"
                    type="password"
                    className={styles.inputControl}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••••••"
                    required
                  />
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="confirm-password">
                  Confirm New Password
                </label>
                <div className={styles.inputWrapper}>
                  <Lock size={17} className={styles.inputIcon} />
                  <input
                    id="confirm-password"
                    type="password"
                    className={styles.inputControl}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="••••••••••••"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={resetPasswordMutation.isPending}
              >
                <span>{resetPasswordMutation.isPending ? 'Updating…' : 'Set New Password'}</span>
                <ArrowRight size={17} />
              </button>
            </form>
          )}

          <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
            <button
              type="button"
              className={styles.link}
              onClick={() => navigate('/login')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              <ArrowLeft size={14} />
              <span>Back to Sign In</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
